import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { ArrowSyncRegular, DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { UserPicker } from '../features/users/UserPicker';
import type { AppDetailDto, AppInventoryDto, AppInventoryItemDto, DirectoryUserDto } from '../api/types';
import './devices.css';

type SortCol = 'app' | 'type' | 'publisher' | 'assigned' | 'groups' | 'installed' | 'users' | 'failed';
type Status = 'cached' | 'updating' | 'live';

const KIND_LABEL: Record<string, string> = {
  group: 'Gruppe',
  allUsers: 'Alle Nutzer',
  allDevices: 'Alle Geräte',
  exclusionGroup: 'Ausschluss-Gruppe',
  other: 'Sonstige',
};

function num(v?: number): string {
  return v == null ? '—' : String(v);
}
function fmt(s?: string): string {
  return s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

export function SoftwareInventoryTab() {
  const api = useApi();
  const [inv, setInv] = useState<AppInventoryDto | null>(null);
  const [status, setStatus] = useState<Status>('updating');
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [assignedFilter, setAssignedFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'app', dir: 'asc' });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AppDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cached = await api.getSoftwareInventory();
        if (active && cached.items.length > 0) {
          setInv(cached);
          setStatus('cached');
        }
      } catch {
        /* Cache leer/fehlend — Refresh füllt. */
      }
      setStatus('updating');
      try {
        const fresh = await api.refreshSoftwareInventory();
        if (active) {
          setInv(fresh);
          setStatus('live');
        }
      } catch (e) {
        if (active) {
          setError(e instanceof Error ? e.message : String(e));
          setStatus('cached');
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [api]);

  async function refresh() {
    setStatus('updating');
    setError(null);
    try {
      setInv(await api.refreshSoftwareInventory());
      setStatus('live');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('cached');
    }
  }

  async function selectApp(id: string) {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await api.getAppDetail(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDetailLoading(false);
    }
  }

  const items = inv?.items ?? [];
  const types = useMemo(() => [...new Set(items.map((i) => i.appType).filter(Boolean) as string[])].sort(), [items]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items
      .filter((i) => typeFilter.size === 0 || (i.appType ? typeFilter.has(i.appType) : false))
      .filter((i) => assignedFilter === 'all' || (assignedFilter === 'yes' ? i.assigned : !i.assigned))
      .filter(
        (i) =>
          q === '' ||
          [i.appName, i.appType, i.publisher, i.version, i.intents].some((f) => (f ?? '').toLowerCase().includes(q)),
      );
    const val = (i: AppInventoryItemDto): string | number => {
      switch (sort.col) {
        case 'app':
          return i.appName.toLowerCase();
        case 'type':
          return (i.appType ?? '').toLowerCase();
        case 'publisher':
          return (i.publisher ?? '').toLowerCase();
        case 'assigned':
          return Number(i.assigned);
        case 'groups':
          return i.groupAssignments;
        case 'installed':
          return i.installedDevices ?? -1;
        case 'users':
          return i.installedUsers ?? -1;
        case 'failed':
          return i.failedDevices ?? -1;
      }
    };
    return [...list].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      const v = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? v : -v;
    });
  }, [items, search, typeFilter, assignedFilter, sort]);

  function head(label: string, col: SortCol) {
    const active = sort.col === col;
    return (
      <th
        style={{ cursor: 'pointer' }}
        onClick={() => setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }))}
      >
        {label}
        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
      <div className="sw-status">
        {status === 'updating' ? (
          <>
            <Spinner size="tiny" /> <span>Inventar wird aktualisiert…</span>
          </>
        ) : status === 'live' ? (
          <Badge appearance="filled" color="success">
            Live
          </Badge>
        ) : (
          <span>Stand: {fmt(inv?.updatedUtc)}</span>
        )}
        {inv?.updatedUtc && status !== 'updating' ? <span>· {fmt(inv.updatedUtc)}</span> : null}
        <Button size="small" appearance="subtle" icon={<ArrowSyncRegular />} disabled={status === 'updating'} onClick={refresh}>
          Aktualisieren
        </Button>
        <span style={{ marginLeft: 'auto' }}>{visible.length} Apps</span>
      </div>

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          style={{ minWidth: 240 }}
          placeholder="App/Publisher/Typ suchen…"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          contentBefore={<SearchRegular />}
        />
        <Dropdown
          multiselect
          style={{ minWidth: 150 }}
          placeholder="Typ: alle"
          value={typeFilter.size ? `Typ (${typeFilter.size})` : 'Typ: alle'}
          selectedOptions={[...typeFilter]}
          onOptionSelect={(_, d) => setTypeFilter(new Set(d.selectedOptions))}
        >
          {types.map((t) => (
            <Option key={t} value={t} text={t}>
              {t}
            </Option>
          ))}
        </Dropdown>
        <div className="asm-toggles" role="group" aria-label="Zuweisung">
          {(['all', 'yes', 'no'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={assignedFilter === v ? 'asm-toggle active' : 'asm-toggle'}
              onClick={() => setAssignedFilter(v)}
            >
              {v === 'all' ? 'Alle' : v === 'yes' ? 'Zugewiesen' : 'Nicht zugewiesen'}
            </button>
          ))}
        </div>
      </div>

      <div className="sw-split" style={{ gridTemplateColumns: selectedId ? '1fr 380px' : '1fr' }}>
        <div className="dev-tablewrap" style={{ maxHeight: '70vh' }}>
          <table className="dev-table">
            <thead>
              <tr>
                {head('App', 'app')}
                {head('Typ', 'type')}
                {head('Publisher', 'publisher')}
                <th>Version</th>
                {head('Zugewiesen', 'assigned')}
                {head('Gruppen', 'groups')}
                <th>All Users</th>
                <th>All Devices</th>
                {head('Installiert', 'installed')}
                {head('Konten', 'users')}
                {head('Fehlgeschl.', 'failed')}
                <th>Intents</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((i) => (
                <tr
                  key={i.appId}
                  style={{ cursor: 'pointer', background: i.appId === selectedId ? tokens.colorNeutralBackground1Selected : undefined }}
                  onClick={() => selectApp(i.appId)}
                >
                  <td>
                    <Text weight="semibold" size={200}>
                      {i.appName}
                    </Text>
                  </td>
                  <td>{i.appType ?? '—'}</td>
                  <td>{i.publisher ?? '—'}</td>
                  <td>{i.version ?? '—'}</td>
                  <td>
                    <Badge appearance="tint" color={i.assigned ? 'success' : 'subtle'}>
                      {i.assigned ? 'ja' : 'nein'}
                    </Badge>
                  </td>
                  <td>{i.groupAssignments}</td>
                  <td>{i.allUsers ? '✓' : '—'}</td>
                  <td>{i.allDevices ? '✓' : '—'}</td>
                  <td>{num(i.installedDevices)}</td>
                  <td>{num(i.installedUsers)}</td>
                  <td>{(i.failedDevices ?? 0) > 0 ? <span style={{ color: '#b3261e' }}>{i.failedDevices}</span> : num(i.failedDevices)}</td>
                  <td>{i.intents ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedId ? (
          <AppDetailPanel
            loading={detailLoading}
            detail={detail}
            onClose={() => {
              setSelectedId(null);
              setDetail(null);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function AppDetailPanel({
  loading,
  detail,
  onClose,
}: {
  loading: boolean;
  detail: AppDetailDto | null;
  onClose: () => void;
}) {
  const api = useApi();
  const [person, setPerson] = useState<DirectoryUserDto | undefined>();
  const [groupId, setGroupId] = useState<string | undefined>();
  const [assignMsg, setAssignMsg] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  const groups = (detail?.assignments ?? []).filter((a) => a.kind === 'group' && a.groupId);

  async function assign() {
    if (!detail || !person || !groupId) return;
    const g = groups.find((x) => x.groupId === groupId);
    setAssigning(true);
    setAssignMsg(null);
    try {
      const res = await api.setSoftwareMembership({
        userId: person.id,
        userDisplay: person.displayName,
        groupId,
        groupName: g?.groupName ?? undefined,
        appName: detail.summary.appName,
        assign: true,
      });
      setAssignMsg(res.status === 'PendingApproval' ? 'Antrag eingereicht (unter „Meine Anfragen").' : 'Zugewiesen.');
    } catch (e) {
      setAssignMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setAssigning(false);
    }
  }

  return (
    <div className="sw-detail">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <Text weight="semibold" size={400}>
            {loading ? 'Lade…' : detail?.summary.appName ?? '—'}
          </Text>
          {detail ? (
            <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
              {detail.summary.appType} {detail.summary.version ? `· ${detail.summary.version}` : ''}
            </Caption1>
          ) : null}
        </div>
        <Button size="small" appearance="subtle" icon={<DismissRegular />} aria-label="Schließen" onClick={onClose} />
      </div>

      {loading ? <Spinner size="tiny" label="Lade Details…" /> : null}

      {detail ? (
        <>
          {/* Metadaten */}
          <div>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Metadaten</Caption1>
            <div style={{ fontSize: 12 }}>
              <div>Publisher: {detail.summary.publisher ?? '—'}</div>
              <div>Erstellt: {fmt(detail.created)}</div>
              <div>Geändert: {fmt(detail.modified)}</div>
              {detail.description ? <div style={{ marginTop: 4, color: tokens.colorNeutralForeground2 }}>{detail.description}</div> : null}
            </div>
          </div>

          {/* Install-Summary */}
          {detail.install ? (
            <div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Install-Summary</Caption1>
              <div className="sw-kpis">
                <Kpi label="installiert" value={detail.install.installed} />
                <Kpi label="fehlgeschl." value={detail.install.failed} tone={detail.install.failed > 0 ? '#b3261e' : undefined} />
                <Kpi label="ausstehend" value={detail.install.pending} />
                <Kpi label="nicht inst." value={detail.install.notInstalled} />
                <Kpi label="Konten ✓" value={detail.install.installedUsers} />
                <Kpi label="Konten ✗" value={detail.install.failedUsers} />
              </div>
            </div>
          ) : null}

          {/* Zuweisungen */}
          <div>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Zuweisungen ({detail.assignments.length})</Caption1>
            <div className="sw-list">
              {detail.assignments.length === 0 ? (
                <Caption1>keine</Caption1>
              ) : (
                detail.assignments.map((a, idx) => (
                  <div className="sw-list-row" key={idx}>
                    <strong>{KIND_LABEL[a.kind] ?? a.kind}</strong>
                    {a.groupName ? <span className="pii"> · {a.groupName}</span> : null}
                    {a.intent ? <span style={{ color: tokens.colorNeutralForeground3 }}> · {a.intent}</span> : null}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Zuweisen */}
          {groups.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Person zuweisen (über Gruppe)</Caption1>
              <UserPicker onSelect={setPerson} placeholder="Person suchen…" />
              <Dropdown
                placeholder="Zuweisungs-Gruppe wählen"
                value={groups.find((g) => g.groupId === groupId)?.groupName ?? ''}
                selectedOptions={groupId ? [groupId] : []}
                onOptionSelect={(_, d) => setGroupId(d.optionValue)}
              >
                {groups.map((g) => (
                  <Option key={g.groupId} value={g.groupId!} text={g.groupName ?? g.groupId!}>
                    {g.groupName ?? g.groupId}
                  </Option>
                ))}
              </Dropdown>
              <Button size="small" appearance="primary" disabled={!person || !groupId || assigning} onClick={assign}>
                {assigning ? 'Bitte warten…' : 'Zuweisen'}
              </Button>
              {assignMsg ? <Caption1>{assignMsg}</Caption1> : null}
            </div>
          ) : null}

          {/* Geräte-Status */}
          <div>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Geräte-Status ({detail.deviceStatuses.length})</Caption1>
            <div className="sw-list">
              {detail.deviceStatuses.length === 0 ? (
                <Caption1>keine Daten</Caption1>
              ) : (
                detail.deviceStatuses.slice(0, 50).map((d, idx) => (
                  <div className="sw-list-row" key={idx}>
                    <span className="pii">{d.deviceName}</span>
                    {d.state ? <span style={{ color: tokens.colorNeutralForeground3 }}> · {d.state}</span> : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="sw-kpi">
      <div className="sw-kpi-value" style={tone ? { color: tone } : undefined}>
        {value}
      </div>
      <div className="sw-kpi-label">{label}</div>
    </div>
  );
}
